import {
  PopUpMessageContext,
  useApp,
  useAuth,
  useCollectionItem,
  usePracticeAccess,
} from '@hb/react'
import {
  AdminUserData,
  authAppealsTemplatesCollection,
  authInstructionsTemplatesCollection,
  claimTemplatesCollection,
  clinicalsTemplatesCollection,
  consentFormTemplatesCollection,
  getAdminTemplateId,
  getMidwifeId,
  getMostRecentAssessment,
  getPregnancyPrimaryCoverage,
  insurersCollection,
  invoiceTemplatesCollection,
  midwivesCollection,
  PopulatedAssessment,
  PregnancyClaim,
  ProfileItem,
  TemplateId,
  TemplateKey,
  User,
  USER_INVITES,
  USERS,
  ViewedTemplate,
} from '@hb/shared'
import { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { getEmailVerified } from '../../../backend/auth/utils'
import { useDocument } from '../../../backend/db/hooks/useDocument'
import { PatientContextData } from '../../../contexts/user/types'
import { useAssessmentClaims } from './usePatientClaims'
import { usePatientEncounters } from './usePatientEncounters'
import { usePatientOfficeVisits } from './usePatientVisits'
import { useAssessmentFromId } from './usePopulatedAssessment'
import { usePopulatedUser } from './usePopulatedUser'
import { useUserAssessments } from './useUserAssessments'
import { useUserConsentForms } from './useUserConsentForms'

export const useAssessmentMidwife = (assessment?: PopulatedAssessment) =>
  useCollectionItem(midwivesCollection, getMidwifeId(assessment)).item

export const useAssessmentInsurer = (assessment?: PopulatedAssessment | null) =>
  useCollectionItem(
    insurersCollection,
    getPregnancyPrimaryCoverage(assessment?.plans)?.insuranceProviderId,
  ).item

export const useAdminItem = (id?: string) => useDocument<AdminUserData>('users-admin', id).data
export const useUserItem = (id?: string, isInvite?: boolean) =>
  useDocument<User>(isInvite ? USER_INVITES : USERS, id).data

const useUserEmailVerified = (id?: string | null) => {
  const [verified, setVerified] = useState(false)
  const [loading, setLoading] = useState(false)
  const { processResponse } = useContext(PopUpMessageContext)

  const refetch = useCallback(() => {
    if (!id) {
      setVerified(false)
      return
    }
    setLoading(true)
    getEmailVerified(id)
      .then(updated => {
        setVerified(updated)
        setLoading(false)
      })
      .catch((err: any) => {
        processResponse({
          error: err?.message || 'Error getting email verified',
        })
        setVerified(false)
        setLoading(false)
      })
  }, [id, processResponse])
  useEffect(refetch, [refetch])

  return { verified, loading, refetch }
}

export const useUserFromId = (
  admin: boolean,
  userId: string | null,
  selectedItem: ProfileItem | null,
): PatientContextData => {
  const { appName } = useApp()
  const claimId = useMemo(
    () => (selectedItem?.type === 'claim' ? selectedItem.id : null),
    [selectedItem],
  )

  const _assessmentId = useMemo(
    () => (selectedItem?.type === 'pregnancy' ? selectedItem.id : null),
    [selectedItem],
  )

  const { data: claim } = useDocument<PregnancyClaim>('claims', claimId)
  const assessmentId = useMemo(
    () => (claimId ? (claim?.assessmentId ?? null) : _assessmentId),
    [claim, _assessmentId, claimId],
  )
  const { authUser: me, admin: isAdmin } = useAuth()
  const selectedAssessment = useAssessmentFromId(admin, me ? assessmentId : undefined)

  const { selectedPracticeId } = usePracticeAccess()
  const { populated } = selectedAssessment ?? {}
  const practiceId = useMemo(
    () => (appName === 'providers-app' ? selectedPracticeId : populated?.midwifeId) ?? null,
    [selectedPracticeId, appName, populated?.midwifeId],
  )

  const { data: assessments } = useUserAssessments(userId, true)
  const encounters = usePatientEncounters(null, userId)
  const data = usePopulatedUser(userId, false, practiceId)

  const mostRecentPregnancy = useMemo(
    () => getMostRecentAssessment(assessments ?? {}),
    [assessments],
  )

  const emailVerification = useUserEmailVerified(
    !data.loading && data.user && !data?.user?.isInvite ? userId : null,
  )
  const insuranceProvider = useAssessmentInsurer(populated)

  const claims = useAssessmentClaims(assessmentId)
  const officeVisits = usePatientOfficeVisits(userId)

  const [viewedTemplateKey, setViewedTemplateKeyState] = useState<TemplateKey | null>(null)
  const [viewedTemplateIdOverride, setViewedTemplateIdOverride] = useState<TemplateId | null>(null)
  const setViewedTemplateKey = useCallback(
    (key: TemplateKey | null, templateId: TemplateId | null = null) => {
      setViewedTemplateKeyState(key)
      setViewedTemplateIdOverride(templateId)
    },
    [],
  )
  const viewedTemplate = useMemo<ViewedTemplate | null>(() => {
    const base = ((): ViewedTemplate | null => {
      switch (viewedTemplateKey) {
        case 'clinicals':
          return {
            type: 'clinicals',
            collection: clinicalsTemplatesCollection,
            id: insuranceProvider?.clinicalsTemplateId
              ? getAdminTemplateId(insuranceProvider?.clinicalsTemplateId)
              : null,
          }
        case 'authAppeals':
          return {
            type: 'authAppeals',
            collection: authAppealsTemplatesCollection,
            id: insuranceProvider?.authAppealsTemplateId
              ? getAdminTemplateId(insuranceProvider?.authAppealsTemplateId)
              : null,
          }
        case 'authInstructions':
          return {
            type: 'authInstructions',
            collection: authInstructionsTemplatesCollection,
            id: insuranceProvider?.authInstructionsTemplateId
              ? getAdminTemplateId(insuranceProvider?.authInstructionsTemplateId)
              : null,
          }
        case 'invoiceAndReceipt':
          return {
            type: 'invoiceAndReceipt',
            collection: invoiceTemplatesCollection,
            id: null,
          }
        case 'claims':
          return {
            type: 'claims',
            collection: claimTemplatesCollection,
            id: insuranceProvider?.claimTemplateId
              ? getAdminTemplateId(insuranceProvider?.claimTemplateId)
              : null,
          }
        case 'consentForm':
          return {
            type: 'consentForm',
            collection: consentFormTemplatesCollection,
            id: null,
          }
        default:
          return null
      }
    })()
    if (base && viewedTemplateIdOverride) return { ...base, id: viewedTemplateIdOverride }
    return base
  }, [viewedTemplateKey, insuranceProvider, viewedTemplateIdOverride])

  const consentForms = useUserConsentForms(userId)

  return useMemo<PatientContextData>(
    () => ({
      ...data,
      userId: userId ?? null,
      practiceId,
      viewedTemplate,
      setViewedTemplateKey,
      assessments,
      admin: isAdmin,
      mostRecentPregnancy,
      pregnancyData: selectedAssessment?.assessmentData?.data ?? {},
      pregnancyCorrections: selectedAssessment?.assessmentData?.corrections ?? {},
      selectedAssessment,
      consentForms,
      emailVerification,
      claims,
      selectedItem,
      officeVisits,
      assessmentId,
      encounters,
    }),
    [
      data,
      userId,
      viewedTemplate,
      practiceId,
      assessments,
      isAdmin,
      selectedAssessment,
      mostRecentPregnancy,
      consentForms,
      emailVerification,
      claims,
      officeVisits,
      assessmentId,
      selectedItem,
      encounters,
    ],
  )
}
